import { useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip,
  Link as MuiLink, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis,
} from 'recharts';
import { usePageTitle } from '../../components/shell/AppShell';
import { useRevenuePortfolio } from '../../api/revenue';
import type {
  DataState, MachineRevenueRow, RevenuePortfolio, SiteRankRow, TrendPoint,
} from '../../api/revenue';
import { fmtCurrencyCents, fmtDate, fmtOrEmDash, fmtPercent } from '../../utils/format';
import { PeriodSelector } from './PeriodSelector';
import type { PeriodValue } from './PeriodSelector';
import { ALL_OPERATORS_SCOPE, RevenueScopeFilters } from './RevenueScopeFilters';
import type { RevenueScopeFilterValue } from './RevenueScopeFilters';
import { dataStateChip, netWinColor, priorPeriodLabel, revenueDelta, siteStatus } from './revenueFormat';
import { KpiCard } from './KpiCard';
import { MiniSparkline } from './MiniSparkline';
import { renderSuspectDot, SuspectTrendCaption } from './TrendSuspectDot';
import { SuspectDaysDialog } from './SuspectDaysDialog';
import { useRevenueExport } from './useRevenueExport';
import { RevenueRefreshButton } from './RevenueRefreshButton';

/**
 * Portfolio-level revenue dashboard (task 9 — UP-MVP wave 1). Period picker
 * + KPI cards + turnover/net-win trend + site ranking + top/needs-attention
 * machine leaderboards, all driven by one `useRevenuePortfolio` call.
 *
 * Two spec-mandated gates (kept as inline conditionals, not separate
 * components, so the gating logic stays visible at the call site):
 *   - F-4 / UP-MVP-040: the "Machines online" KPI + per-site online column
 *     render ONLY when `dataState.currentPeriod` — connectivity is a
 *     *live* signal and must never sit unlabelled next to a historical
 *     revenue window.
 *   - F-11: the trend chart is hidden for `trend.length <= 1` (Today /
 *     Yesterday are single-gaming-day periods) — never render an
 *     intraday/empty chart.
 */
export default function RevenuePortfolioPage() {
  usePageTitle('Revenue');

  const download = useRevenueExport();
  const [period, setPeriod] = useState<PeriodValue>({ token: 'today' });
  // Operator/site filter-bar selection — independent of `period` (selections
  // survive period changes) and independent of the review-dialog state. Kept
  // as local component state, same as `period` itself: this page doesn't
  // sync any of its filters to the URL today (no `useSearchParams` in use
  // here), so the new operator/site filters follow the existing pattern
  // rather than introducing a one-off URL-sync mechanism for just these two.
  const [scope, setScope] = useState<RevenueScopeFilterValue>(ALL_OPERATORS_SCOPE);
  const [reviewOpen, setReviewOpen] = useState(false);
  const q = useRevenuePortfolio(
    period.token, period.from, period.to,
    scope.operatorId ?? undefined, scope.siteId ?? undefined,
  );
  const portfolio = q.data;

  const noData = !!portfolio
    && portfolio.totals.turnoverCents === 0
    && portfolio.totals.totalOutCents === 0
    && portfolio.totals.jackpotsCents === 0
    && portfolio.totals.netWinCents === 0
    && portfolio.siteRanking.length === 0;

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <PeriodSelector value={period} onChange={setPeriod} />
          <RevenueScopeFilters value={scope} onChange={setScope} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <RevenueRefreshButton isFetching={q.isFetching} onRefresh={() => { void q.refetch(); }} />
          <Button
            size="small" variant="outlined" disabled={!portfolio}
            onClick={() => download(
              'portfolio', null, period.token, period.from, period.to, 'csv',
              scope.operatorId ?? undefined, scope.siteId ?? undefined,
            )}
          >
            Export CSV
          </Button>
          <Button
            size="small" variant="outlined" disabled={!portfolio}
            onClick={() => download(
              'portfolio', null, period.token, period.from, period.to, 'pdf',
              scope.operatorId ?? undefined, scope.siteId ?? undefined,
            )}
          >
            Export PDF
          </Button>
        </Stack>
      </Stack>

      {q.isLoading && (
        <Typography variant="body2" color="text.secondary">Loading revenue…</Typography>
      )}
      {q.isError && (
        <Alert severity="error">
          {(q.error as { message?: string })?.message ?? 'Failed to load revenue data.'}
        </Alert>
      )}

      {portfolio && (
        <>
          <Typography variant="body2" color="text.secondary">{portfolio.scopeDescription}</Typography>

          {noData ? (
            <Alert severity="info">No revenue data for the selected scope and period.</Alert>
          ) : (
            <>
              {portfolio.dataState.provisional && (
                <ProvisionalBanner
                  dataState={portfolio.dataState}
                  onReview={() => setReviewOpen(true)}
                />
              )}

              {reviewOpen && (
                <SuspectDaysDialog
                  open
                  onClose={() => setReviewOpen(false)}
                  period={period.token}
                  from={period.from}
                  to={period.to}
                />
              )}

              <KpiRow portfolio={portfolio} />

              {portfolio.trend.length > 1 && <TrendChart trend={portfolio.trend} />}

              <SiteRankingTable rows={portfolio.siteRanking} dataState={portfolio.dataState} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <MachineTable
                  title="Top machines" rows={portfolio.topMachines} dataState={portfolio.dataState}
                  emptyMessage="No machines to show."
                />
                <MachineTable
                  title="Needs attention" rows={portfolio.needsAttention} dataState={portfolio.dataState}
                  emptyMessage="No machines flagged."
                />
              </Box>
            </>
          )}
        </>
      )}
    </Stack>
  );
}

function onlineLabel(online: number | null, total: number | null): string {
  return online != null && total != null ? `${online}/${total}` : fmtOrEmDash(null);
}

const TURNOVER_COLOR = '#1976d2';
const NETWIN_COLOR = '#2e7d32';

function KpiRow({ portfolio }: { portfolio: RevenuePortfolio }) {
  const { totals, priorTotals, dataState, machinesOnline, machinesTotal, trend, periodToken } = portfolio;
  const chip = dataStateChip(dataState);
  const priorLabel = priorPeriodLabel(periodToken);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
      <KpiCard
        label="Turnover"
        value={fmtCurrencyCents(totals.turnoverCents)}
        showDelta
        delta={revenueDelta(totals.turnoverCents, priorTotals?.turnoverCents ?? null)}
        deltaLabel={priorLabel}
        sparkline={trend.map((t) => t.turnoverCents)}
        sparklineColor={TURNOVER_COLOR}
      />
      <KpiCard
        label="Net win"
        value={fmtCurrencyCents(totals.netWinCents)}
        valueColor={netWinColor(totals.netWinCents)}
        secondary={`Hold ${fmtPercent(totals.holdPct)}`}
        showDelta
        delta={revenueDelta(totals.netWinCents, priorTotals?.netWinCents ?? null)}
        deltaLabel={priorLabel}
        sparkline={trend.map((t) => t.netWinCents)}
        sparklineColor={NETWIN_COLOR}
      />
      {/* F-4 / UP-MVP-040 — current-period only. No prior availability data, so no delta. */}
      {dataState.currentPeriod && (
        <KpiCard label="Machines online" value={onlineLabel(machinesOnline, machinesTotal)} />
      )}
      <KpiCard label="Data health" value={chip.label} />
    </Box>
  );
}

function TrendChart({ trend }: { trend: TrendPoint[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Turnover vs net win</Typography>
      <Box data-testid="revenue-trend-chart" sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="gamingDay" tickFormatter={(v) => fmtDate(v)} minTickGap={30} />
            {/* Net win is only a few % of turnover (the hold), so it shares no
                useful scale with turnover — give it its own right-hand axis so
                its shape (negative some days, positive most) is legible. Axes are
                coloured to match their lines. */}
            <YAxis yAxisId="turnover" tickFormatter={(v) => fmtCurrencyCents(v)} width={90} stroke="#1976d2" />
            <YAxis yAxisId="netwin" orientation="right" tickFormatter={(v) => fmtCurrencyCents(v)} width={90} stroke="#2e7d32" />
            <RechartsTooltip
              labelFormatter={(v) => fmtDate(v)}
              formatter={(value) => fmtCurrencyCents(Number(value))}
            />
            <Legend />
            {/* dot renders a red ring only on suspect gaming days (included but
                flagged for review); all other points draw nothing. */}
            <Line yAxisId="turnover" type="monotone" dataKey="turnoverCents" name="Turnover" stroke="#1976d2" strokeWidth={2} dot={renderSuspectDot} activeDot={{ r: 4 }} />
            <Line yAxisId="netwin" type="monotone" dataKey="netWinCents" name="Net win" stroke="#2e7d32" strokeWidth={2} dot={renderSuspectDot} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      <SuspectTrendCaption />
    </Paper>
  );
}

function ProvisionalBanner({ dataState, onReview }: {
  dataState: DataState; onReview: () => void;
}) {
  return (
    <Alert severity="warning" variant="outlined">
      <AlertTitle>Provisional data</AlertTitle>
      {dataState.currentPeriod && (
        <Typography variant="body2">
          This period includes today, which is still being collected and may change.
        </Typography>
      )}
      {dataState.affectedCount > 0 && (
        <Box sx={{ mt: dataState.currentPeriod ? 1 : 0 }}>
          <Button variant="text" color="warning" onClick={onReview} sx={{ textTransform: 'none', p: 0, textAlign: 'left' }}>
            {dataState.affectedCount} machine{dataState.affectedCount === 1 ? ' has' : 's have'} suspect
            readings in this period — included in the totals but flagged for review. Review suspect meter-days →
          </Button>
        </Box>
      )}
    </Alert>
  );
}

function SiteStatusCell({ online, total }: { online: number | null; total: number | null }) {
  const status = siteStatus(online, total);
  if (status === null) return <>{fmtOrEmDash(null)}</>;
  return <Chip size="small" label={status.label} color={status.color} />;
}

function SiteTrendCell({ row }: { row: SiteRankRow }) {
  // Net-win series to match the net-win ranking. A single point conveys no
  // trend — show an em-dash instead of a flat dot.
  if (row.trend.length <= 1) return <>{fmtOrEmDash(null)}</>;
  return <MiniSparkline data={row.trend.map((t) => t.netWinCents)} color={NETWIN_COLOR} ariaLabel={`${row.siteName} trend`} />;
}

function SiteRankingTable({ rows, dataState }: { rows: SiteRankRow[]; dataState: DataState }) {
  const chip = dataStateChip(dataState);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Site ranking</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No sites in this period.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="site ranking" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Site</TableCell>
                {/* Status + Machines are live signals — current-period only (F-4). */}
                {dataState.currentPeriod && <TableCell>Status</TableCell>}
                <TableCell align="right">Net win</TableCell>
                <TableCell align="right">Turnover</TableCell>
                <TableCell align="right">Hold</TableCell>
                {dataState.currentPeriod && <TableCell align="right">Machines</TableCell>}
                <TableCell sx={{ width: 96 }}>Trend</TableCell>
                <TableCell>Data state</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {rows.map((r) => (
                <TableRow key={r.siteId} hover>
                  <TableCell>
                    <MuiLink component={RouterLink} to={`/revenue/sites/${r.siteId}`}>{r.siteName}</MuiLink>
                  </TableCell>
                  {dataState.currentPeriod && (
                    <TableCell><SiteStatusCell online={r.machinesOnline} total={r.machinesTotal} /></TableCell>
                  )}
                  <TableCell align="right">
                    <Typography component="span" sx={{ color: netWinColor(r.netWinCents) }}>
                      {fmtCurrencyCents(r.netWinCents)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.turnoverCents)}</TableCell>
                  <TableCell align="right">{fmtPercent(r.holdPct)}</TableCell>
                  {dataState.currentPeriod && (
                    <TableCell align="right">{onlineLabel(r.machinesOnline, r.machinesTotal)}</TableCell>
                  )}
                  <TableCell sx={{ width: 96 }}><SiteTrendCell row={r} /></TableCell>
                  <TableCell><Chip size="small" label={chip.label} color={chip.color} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

function MachineTable({ title, rows, dataState, emptyMessage }: {
  title: string; rows: MachineRevenueRow[]; dataState: DataState; emptyMessage: string;
}) {
  const chip = dataStateChip(dataState);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={title} sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Game</TableCell>
                <TableCell>Site</TableCell>
                <TableCell align="right">Net win</TableCell>
                <TableCell align="right">Turnover</TableCell>
                <TableCell align="right">Hold</TableCell>
                <TableCell>Data state</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {rows.map((r) => (
                <TableRow key={`${r.machineId}:${r.versionId}`} hover>
                  <TableCell>
                    {/* Owner request: leaderboards show the game name (easier to
                        recognise than the asset number); hover reveals the asset,
                        and machines with no game_name fall back to it. Each row is
                        ONE CONFIG (game version) — a cabinet whose config changed
                        (e.g. after a RAM reset) shows one row per config, never a
                        combined sum (owner rule, 2026-09-25). */}
                    <MuiLink
                      component={RouterLink}
                      to={`/revenue/machines/${r.machineId}?versionId=${r.versionId}`}
                      title={`${r.machineLabel} — config v${r.versionNo}`}
                    >
                      {r.gameName ?? r.machineLabel} (v{r.versionNo})
                    </MuiLink>
                  </TableCell>
                  <TableCell>
                    <MuiLink component={RouterLink} to={`/revenue/sites/${r.siteId}`}>{r.siteName}</MuiLink>
                  </TableCell>
                  <TableCell align="right">
                    <Typography component="span" sx={{ color: netWinColor(r.netWinCents) }}>
                      {fmtCurrencyCents(r.netWinCents)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtCurrencyCents(r.turnoverCents)}</TableCell>
                  <TableCell align="right">{fmtPercent(r.holdPct)}</TableCell>
                  <TableCell><Chip size="small" label={chip.label} color={chip.color} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
